import React, { useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { ScrollView, StyleSheet } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { theme } from '@garnish/constants';

import { useIsLoggedIn } from '@order/AuthMachine';
import { NotFoundView } from '@order/components';
import { useCustomer } from '@order/Customer';
import { TierName } from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { useToggleScreenHeader } from '../../navigation';
import type { SweetpassHomeScreenProps } from '../../navigation/AppNavigation.props';
import type { SweetpassHeroTierName } from './components';
import { SweetpassHomeContainer, SweetpassHomeLoading } from './components';
import { useSweetpassHomeContentfulData } from './hooks';
import { SweetpassHomeScreenSignedOut } from './SweetpassHomeScreenSignedOut';

/**
 * Renders the Sweetpass Home content if all of these are true.
 * - Feature flag is on.
 * - Customer is logged in.
 * - Tier is not opted-out.
 * - Contentful data is loaded.
 */
export const SweetpassHomeScreen = (props: SweetpassHomeScreenProps) => {
  useToggleScreenHeader();

  return (
    <ScrollView
      style={styles.scrollContainer}
      contentContainerStyle={styles.scrollContent}
    >
      <SweetpassFeatureFlagGuard>
        <SweetpassAuthGuard>
          <SweetpassHomeScreenPostGuards {...props} />
        </SweetpassAuthGuard>
      </SweetpassFeatureFlagGuard>
    </ScrollView>
  );
};

/**
 * Prevent render if feature flag is off.
 */
const SweetpassFeatureFlagGuard = (props: {
  readonly children: React.ReactElement;
}) => {
  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled', {
    listenForChanges: true,
  });

  if (!isSweetpassEnabled) return <NotFoundView />;

  return props.children;
};

/**
 * Prevent render if customer is logged out.
 */
const SweetpassAuthGuard = (props: {
  readonly children: React.ReactElement;
}) => {
  const isLoggedIn = useIsLoggedIn();

  if (!isLoggedIn) return <SweetpassHomeScreenSignedOut />;

  return props.children;
};

/**
 * Prevent render if tier is opted out.
 * Prevent render if contentful data isn't loaded.
 */
const SweetpassHomeScreenPostGuards = (
  props: SweetpassHomeScreenProps & ViewProps,
) => {
  const { customer } = useCustomer();
  const {
    isSweetpassTierLoading,
    sweetpassTierName,
    joinDate,
    refetchSweetpassTier,
  } = useSweetpassTier('cache-and-network');

  const { isLoadingContentfulData, sweetpassHomeContentfulData } =
    useSweetpassHomeContentfulData({
      firstName: customer.firstName,
      sweetpassTier: sweetpassTierName,
      joinDate,
    });

  // Requires focus effect to trigger on returning to the Loyalty Tab.
  useFocusEffect(
    useCallback(() => {
      if (sweetpassTierName !== TierName.OptedOut) return;

      // We replace first with home because closing the Sweetpass Upgrade screen would lead to an infinite loop.
      // It needs to be a replace instead of a navigate because opening this tab again won't trigger the focus effect.
      props.navigation.replace('MainTabs', {
        screen: 'HomeTab',
        params: { screen: 'Home' },
      });
      props.navigation.navigate('Modal', { screen: 'SweetpassUpgrade' });
    }, [props.navigation, sweetpassTierName]),
  );

  useFocusEffect(refetchSweetpassTier);

  if (isSweetpassTierLoading || isLoadingContentfulData) {
    return <SweetpassHomeLoading />;
  }

  if (sweetpassTierName === TierName.OptedOut) return null;

  return (
    <SweetpassHomeContainer
      // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style -- @REVIEW
      sweetpassTierName={sweetpassTierName as SweetpassHeroTierName}
      {...sweetpassHomeContentfulData}
      {...props}
    />
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
  scrollContent: {
    height: '100%',
  },
});
